if (typeof WeeklySpecialsPage !== 'function') {

  // FINAL category set (no "Made By Denninger's", no "Produce")
  const CATEGORIES = [
    { slug: "all",                 label: "All Specials" },
    { slug: "butcher",             label: "Butcher" },
    { slug: "deli",                label: "Deli" },
    { slug: "cheese-dairy",        label: "Cheese & Dairy" },
    { slug: "bakery",              label: "Bakery" },
    { slug: "grocery",             label: "Grocery" },
    { slug: "sweets",              label: "Sweets" },
    { slug: "sausages-burgers",    label: "Sausages & Burgers" },
    { slug: "entrees-sides",       label: "Entrées & Sides" },
    { slug: "quiche-pies",         label: "Quiche & Pies" },
    { slug: "soups",               label: "Soups" },
    { slug: "appetizers",          label: "Appetizers" }
  ];
  const CATEGORY_LABEL_BY_SLUG = Object.fromEntries(CATEGORIES.map(c => [c.slug, c.label]));

  // Aliases normalize whatever the sheet contains
  const CATEGORY_ALIASES = {
    // renamed
    "cheese":               "cheese-dairy",
    "cheese & dairy":       "cheese-dairy",
    "cheese and dairy":     "cheese-dairy",

    // new
    "grocery":              "grocery",
    "sweets":                "sweets",
    "sausages & burgers":   "sausages-burgers",
    "sausage & burgers":    "sausages-burgers",
    "sausages and burgers": "sausages-burgers",
    "entrées & sides":      "entrees-sides",
    "entrees & sides":      "entrees-sides",
    "entrees and sides":    "entrees-sides",
    "quiche & pies":        "quiche-pies",
    "quiche and pies":      "quiche-pies",
    "soups":                "soups",
    "appetizers":           "appetizers",

    // keep core
    "butcher":              "butcher",
    "deli":                 "deli",
    "bakery":               "bakery",

    // removed categories → map to Grocery so old sheet entries won't break
    "made by denninger's":  "grocery",
    "made by denningers":   "grocery",
    "produce":              "grocery"
  };

  const fmt = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" });

  function slugify(v = "") {
    return v.toString().trim().toLowerCase()
      .replace(/&/g, "and")
      .replace(/[’']/g, "")
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9\-]/g, "");
  }

  function normalizeCategory(raw) {
    const t = (raw ?? "").toString().trim().toLowerCase();
    if (CATEGORY_ALIASES[t]) return CATEGORY_ALIASES[t];
    const s = slugify(t).replace(/-and-/g, "-");
    if (CATEGORY_ALIASES[s]) return CATEGORY_ALIASES[s];
    return CATEGORY_LABEL_BY_SLUG[s] ? s : "grocery"; // default bucket
  }

  function displayLabelFromSlug(slug) {
    return CATEGORY_LABEL_BY_SLUG[slug] || slug.replace(/-/g, " ");
  }

  function safeNumber(v) {
    const n = parseFloat(String(v).replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : NaN;
  }

  function calcDiscount(orig, sale) {
    if (!Number.isFinite(orig) || !Number.isFinite(sale) || orig <= 0 || sale <= 0) return null;
    const pct = Math.round(((orig - sale) / orig) * 100);
    return Number.isFinite(pct) && pct > 0 ? pct : null;
  }

  class WeeklySpecialsPage extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      this.sheetId = this.dataset.sheetId;
      this.sheetName = this.dataset.sheetName || "Weekly Specials";
      this.dateColumnIndex = parseInt(this.dataset.dateColumnIndex, 10);
      if (!Number.isInteger(this.dateColumnIndex)) this.dateColumnIndex = 0;

      this.effectiveDatesEl = this.querySelector(".weekly-specials__effective-dates");
      this.filtersEl = this.querySelector(".weekly-specials__filters");
      this.gridEl = this.querySelector(".weekly-specials__grid");
      this.emptyEl = this.querySelector(".weekly-specials__empty");
      this.errorEl = this.querySelector(".weekly-specials__error");

      this.state = { items: [] };

      this.renderFilters();
      this.bindEvents();
      this.loadData();
    }

    bindEvents() {
      if (this._eventsBound) return;
      this._eventsBound = true;
      this.filtersEl.addEventListener("click", (e) => {
        const btn = e.target.closest(".weekly-specials__filter");
        if (!btn) return;
        this.applyFilter(btn.dataset.filter, btn);
      });
    }

    async fetchGVizJSON(sheetName) {
      const url = `https://docs.google.com/spreadsheets/d/${this.sheetId}/gviz/tq?tqx=out:json&sheet=${encodeURIComponent(sheetName)}`;
      const res = await fetch(url, { cache: "no-store" });
      const text = await res.text();
      const match = text.match(/setResponse\(([\s\S]*)\);?/);
      if (!match) throw new Error("Unexpected GViz response");
      return JSON.parse(match[1]);
    }

    tableToRows(gviz) {
      const cols = gviz.table.cols;
      const rows = gviz.table.rows.map(r => (r.c || []).map(c => c?.v ?? ""));
      return { cols, rows };
    }

    findColumnIndices(cols) {
      const labels = cols.map(c => c.label || "");
      return {
        category: labels.findIndex(h => /category/i.test(h)),
        name:     labels.findIndex(h => /name/i.test(h)),
        desc:     labels.findIndex(h => /desc|description/i.test(h)),
        regular:  labels.findIndex(h => /original|regular|reg(ular)? price/i.test(h)),
        sale:     labels.findIndex(h => /sale|special ?price/i.test(h)),
        uom:      labels.findIndex(h => /\buom\b|unit of measure/i.test(h)),
        promo:    labels.findIndex(h => /promo|badge/i.test(h))
      };
    }

    getEffectiveDateText(cols) {
      const col = cols[this.dateColumnIndex];
      return (col && col.label ? col.label : "").toString().trim();
    }

    renderEffectiveDates(text) {
      this.effectiveDatesEl.textContent = `Prices Effective from: ${text || "Please update dates in Google Sheet"}`;
    }

    renderError(message) {
      this.effectiveDatesEl.textContent = "";
      this.filtersEl.innerHTML = "";
      this.gridEl.innerHTML = "";
      this.emptyEl.style.display = "none";
      this.errorEl.textContent = message;
      this.errorEl.style.display = "block";
    }

    renderFilters() {
      this.filtersEl.innerHTML = CATEGORIES.map((c, i) => (
        `<button class="weekly-specials__filter" data-filter="${c.slug}" aria-pressed="${i === 0 ? 'true' : 'false'}">${c.label}</button>`
      )).join("");
    }

    renderItems(items) {
      if (!items.length) {
        this.gridEl.innerHTML = "";
        this.emptyEl.style.display = "block";
        return;
      }
      this.emptyEl.style.display = "none";
      this.gridEl.innerHTML = items.map(it => {
        const discount = calcDiscount(it.originalPrice, it.salePrice);
        const badgeText = it.promoLabel || (discount ? `${discount}% OFF` : `SALE`);
        return `
        <article class="weekly-specials__card" data-category="${it.category}">
          <div class="weekly-specials__card-header">
            <span class="weekly-specials__category">${displayLabelFromSlug(it.category)}</span>
            <span class="weekly-specials__badge" title="${badgeText}">${badgeText}</span>
          </div>
          <h3 class="weekly-specials__name">${it.name}</h3>
          <p class="weekly-specials__desc">${it.description || ""}</p>
          <div class="weekly-specials__price-row">
            ${Number.isFinite(it.originalPrice) ? `<span class="weekly-specials__price--original">${fmt.format(it.originalPrice)}</span>` : ""}
            ${Number.isFinite(it.salePrice) ? `<span class="weekly-specials__price--sale">${fmt.format(it.salePrice)}</span>` : ""}
            ${it.uom ? `<span class="weekly-specials__uom">${it.uom}</span>` : ""}
          </div>
        </article>`;
      }).join("");
    }

    applyFilter(category, sourceBtn) {
      this.filtersEl.querySelectorAll(".weekly-specials__filter").forEach(btn => btn.setAttribute("aria-pressed", "false"));
      if (sourceBtn) sourceBtn.setAttribute("aria-pressed", "true");
      const list = category === "all" ? this.state.items : this.state.items.filter(x => x.category === category);
      this.renderItems(list);
      location.hash = `#${category}`;
    }

    async loadData() {
      try {
        this.errorEl.style.display = "none";
        const gviz = await this.fetchGVizJSON(this.sheetName);
        const { cols, rows } = this.tableToRows(gviz);
        const idx = this.findColumnIndices(cols);

        this.renderEffectiveDates(this.getEffectiveDateText(cols));

        const items = rows
          .filter(r => r.some(v => String(v).trim() !== ""))
          .map(r => {
            const category = normalizeCategory(r[idx.category] ?? "grocery");
            const name     = (r[idx.name] ?? "").toString().trim() || "Product";
            const desc     = (r[idx.desc] ?? "").toString().trim();
            const orig     = safeNumber(r[idx.regular]);
            const sale     = safeNumber(r[idx.sale]);
            const uom      = (r[idx.uom] ?? "").toString().trim();
            const promoLabel = (r[idx.promo] ?? "").toString().trim();
            return {
              category,
              name,
              description: desc,
              originalPrice: Number.isFinite(orig) ? orig : NaN,
              salePrice: Number.isFinite(sale) ? sale : NaN,
              uom,
              promoLabel
            };
          })
          .filter(x => Number.isFinite(x.salePrice) || Number.isFinite(x.originalPrice));

        this.state.items = items;

        const requested = location.hash.replace("#", "");
        const startSlug = requested && CATEGORY_LABEL_BY_SLUG[requested] ? requested : "all";
        const btn = this.filtersEl.querySelector(`.weekly-specials__filter[data-filter="${CSS.escape(startSlug)}"]`) || this.filtersEl.querySelector(`.weekly-specials__filter[data-filter="all"]`);
        this.applyFilter(btn.dataset.filter, btn);
      } catch (err) {
        console.error("Failed to load specials:", err);
        this.renderError("We couldn't load this week's specials. Please check back soon.");
      }
    }
  }

  if (typeof customElements.get("weekly-specials-page") == "undefined") {
    customElements.define("weekly-specials-page", WeeklySpecialsPage);
  }

  document.addEventListener("shopify:section:load", (e) => {
    e.target.querySelector("weekly-specials-page")?.init();
  });
}
